import React, { useMemo, useRef, useState } from "react";
import { ListChecks, X, Search } from "lucide-react";
import { PlovouciNabidka } from "./ui.jsx";

// Vyhledávací výběr úkolu z Operations (17.7.2026) — náhrada za dlouhý <select>.
// Napíšeš kus názvu (bez ohledu na diakritiku), vybereš klikem nebo Enterem.
// Používá stránka Time report i stopky v horní liště.

const STATUS_LABELS = { zasobnik: "Backlog", prioritni: "Prioritní", mesicni: "Měsíční", tydenni: "Týdenní", v_procesu: "V procesu" };
const fold = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// vsechny = nabidni cely seznam abecedne, bez preference Tydennich a bez
// useknuti na 30 polozkach (19.8.2026, sekce Odlozeno v meetingu).
export default function UkolPicker({ tasks = [], value, onChange, placeholder = "Najít úkol… (nepovinné)", className = "w-full sm:w-96", vsechny = false }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = tasks.find((t) => String(t.id) === String(value)) || null;

  const filtered = useMemo(() => {
    const f = fold(q.trim());
    const arr = f ? tasks.filter((t) => fold(`${t.nazev} ${t.pilot || ""}`).includes(f)) : tasks;
    // Týdenní a V procesu nahoře — to jsou úkoly, na kterých se teď reálně dělá.
    // S `vsechny` se řadí čistě abecedně, ať je vidět i Backlog a Měsíční.
    const vaha = (t) => (vsechny ? 0 : t.status === "v_procesu" ? 0 : t.status === "tydenni" ? 1 : 2);
    const serazene = [...arr].sort((a, b) => vaha(a) - vaha(b) || String(a.nazev).localeCompare(String(b.nazev), "cs"));
    return vsechny ? serazene.slice(0, 300) : serazene.slice(0, 30);
  }, [tasks, q, vsechny]);

  // FW-149 (14. 10. 2026): nabídka se kreslí přes PlovouciNabidka (portál, fixed).
  // V detailu meetingu ji jako absolutní blok překryla karta Poznámky pod ní,
  // takže hledání vypadalo, že nic nenašlo. Zavření klepnutím mimo řeší ona.

  const pick = (t) => { onChange(String(t.id)); setQ(""); setOpen(false); };

  if (selected) {
    return (
      <div className={`flex items-center gap-2 ${className} bg-brand-50 border border-brand-200 rounded-xl px-2.5 py-2`}>
        <ListChecks className="w-4 h-4 text-brand-600 shrink-0" />
        <span className="text-sm font-semibold text-brand-900 truncate flex-1">{selected.nazev}</span>
        <span className="text-[11px] text-brand-500 shrink-0">{STATUS_LABELS[selected.status] || selected.status}</span>
        <button type="button" onClick={() => { onChange(""); setQ(""); }} className="p-0.5 rounded hover:bg-brand-100 text-brand-500 shrink-0" title="Zrušit výběr úkolu">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); if (filtered[0]) pick(filtered[0]); }
          if (e.key === "Escape") setOpen(false);
        }}
        placeholder={placeholder}
        className="field w-full pl-8 pr-3"
      />
      <PlovouciNabidka kotva={ref} otevreno={open && (filtered.length > 0 || !!q.trim())} onZavrit={() => setOpen(false)} maxVyska={256} className="py-1">
        {filtered.length > 0 ? (
          filtered.map((t) => (
            <button key={t.id} type="button" onClick={() => pick(t)}
              className="w-full flex items-center gap-2 text-left px-3 py-1.5 hover:bg-brand-50">
              <span className="text-sm text-slate-800 truncate flex-1">{t.nazev}</span>
              {t.pilot && <span className="text-[11px] text-slate-400 shrink-0 demo-sensitive">{t.pilot}</span>}
              <span className={`text-[11px] font-semibold shrink-0 ${t.status === "v_procesu" ? "text-brand-600" : t.status === "tydenni" ? "text-rose-600" : "text-slate-400"}`}>
                {STATUS_LABELS[t.status] || t.status}
              </span>
            </button>
          ))
        ) : (
          <div className="px-3 py-2 text-sm text-slate-400">Nic nenalezeno.</div>
        )}
      </PlovouciNabidka>
    </div>
  );
}
